/* 그림·툴패스(engrave) 예제 S3 저장소 — 드로잉/레이저 탭용 공식 예제.
   앱 요청서 BE-0007 + 앱 이식 계획서(ui-handoff-merge-plan §P1/P2) 계약 구현.

   ★ manifest 분리 (중요 — 파이썬과 같은 이유)
     앱 AssetKind enum 은 { blocklyCode, aiModel } 뿐이라 그림·툴패스도 최상위 kind 는
     blocklyCode 로 발행한다(extras.domain/payload 로 구분). 그런데 현행 앱은
     official/manifest.json 의 항목을 **전부 블록 코딩 예제로** 취급하므로, 여기에
     그림(PNG)을 넣으면 코드 예제 목록에 섞여 나오고 열면 실패한다. 따라서:
       · 코드(블록)  → official/manifest.json
       · 코드(파이썬) → official/manifest.python.json
       · 그림·툴패스  → official/manifest.engrave.json   ← 이 파일
     앱이 engrave 섹션을 소비하는 라운드(R-0767)에서 이 manifest 를 추가로 읽는다.
     구버전 앱은 이 파일을 아예 모르므로 회귀가 없다.

   ★ 객체 키 (payload 별 파일명 — 운영 결정)
       그림   official/code/{id}/source.<ext>     (ext = extras.source_ext)
       툴패스 official/code/{id}/project.huenit   (평문 JSON)
       공통   official/code/{id}/meta.json · thumbnail.<ext>
     prefix 를 official/code/ 로 맞춰 썸네일·삭제 경로 유틸을 그대로 재사용한다. */
import { putObject, deleteObject } from '../../api/s3.js';
import { toAssetMeta, fromAssetMeta, buildManifest, normalizeSourceExt } from '../../data/mappers.js';
import { CODE_BUCKET_URL } from '../../config/env.js';
import { computeReorder, getOrder, compareByOrder } from '../examples/ordering.js';
import {
  readAssets, sha256Hex, codeKeyFromUrl,
  uploadExampleThumbnail, bumpExamplesVersion,
} from '../examples/repo.js';
import { validateBuffer, contentTypeFor } from './validate.js';

export const ENGRAVE_MANIFEST_KEY = 'official/manifest.engrave.json';

/* CMS kind(picture|toolpath) → 객체 파일명. */
export function objectNameFor(kind, sourceExt) {
  if (kind === 'toolpath') return 'project.huenit';
  return 'source.' + (normalizeSourceExt(sourceExt) || 'png');
}
const objKey = (id, name) => `official/code/${id}/${name}`;
const metaKey = (id) => `official/code/${id}/meta.json`;

/* meta → payload('picture'|'project'). 없으면 picture(그림)로 본다. */
const payloadOf = (m) => ((m && m.extras && m.extras.payload) || 'picture');

/* manifest 기록 순서 — payload 로 묶고(섹션별 연속) 그 안에서 배치 순서.
   앱이 payload 로 걸러도 각 섹션의 상대 순서는 그대로 보존된다. */
const PAYLOAD_RANK = { picture: 0, project: 1 };
const rank = (m) => (PAYLOAD_RANK[payloadOf(m)] != null ? PAYLOAD_RANK[payloadOf(m)] : 9);
function sortEngrave(assets) {
  return [...(assets || [])].sort((a, b) => (rank(a) - rank(b)) || compareByOrder(a, b));
}

/* 예제 파일 공개 URL (다운로드용) — official/* 은 public-read. */
export function engraveFileUrl(example) {
  return (CODE_BUCKET_URL || '') + '/' + objKey(example.id, objectNameFor(example.kind, example.sourceExt));
}

/* 목록 — official/manifest.engrave.json 전량(그림+툴패스). 화면이 payload 로 나눠 쓴다. */
export async function listEngraveExamples() {
  const assets = await readAssets(ENGRAVE_MANIFEST_KEY);
  return sortEngrave(assets.filter(Boolean))
    .map((meta) => ({ ...fromAssetMeta(meta), _meta: meta }));
}

/* 사용 대상(드로잉/레이저) 규칙 — 그림은 1개 이상(양쪽 공용 허용), 툴패스는 정확히 1개.
   툴패스는 출력 파라미터(레이저 파워·속도 / 펜 Z)가 레이어에 박혀 있어 한쪽 전용이다. */
export function validateEngraveKinds(kind, engraveKinds) {
  const n = (engraveKinds || []).length;
  if (kind === 'toolpath' && n !== 1) {
    return '툴패스 예제는 사용 대상을 드로잉·레이저 중 하나만 지정해야 합니다.';
  }
  if (kind === 'picture' && n < 1) {
    return '그림 예제는 사용 대상을 하나 이상 선택하세요.';
  }
  return '';
}

/* 등록/수정 (RMW) — examples/repo.js saveExample 과 동일한 순서·안전장치.
   example = { id, kind:'picture'|'toolpath', engraveKinds:[], sourceExt, file, catId,
               filterIds, title, desc, thumbnail, orderGroup, orderIndex } */
export async function saveEngraveExample(getCredentials, { example, fileBuffer, thumbnailFile, removeThumbnail, owner = {}, onStep }) {
  const id = example.id;
  const kind = example.kind === 'toolpath' ? 'toolpath' : 'picture';
  const step = (s) => { if (onStep) onStep(s); };

  const kindErr = validateEngraveKinds(kind, example.engraveKinds);
  if (kindErr) throw new Error(kindErr);

  step('목록 확인 중…');
  const assets = await readAssets(ENGRAVE_MANIFEST_KEY);   // 쓰기 전 read = orphan 방지
  const prev = assets.find((a) => a.id === id) || null;
  const prevEx = prev ? fromAssetMeta(prev) : null;

  // 썸네일(선택): 제거 / 새 업로드 / 기존 유지. 제거·교체 시 기존 객체 정리.
  let thumbnail = example.thumbnail || (prev && prev.extras && prev.extras.thumbnail) || '';
  if (removeThumbnail) {
    if (thumbnail) { try { await deleteObject(getCredentials, codeKeyFromUrl(thumbnail)); } catch (e) { /* ignore */ } }
    thumbnail = '';
  } else if (thumbnailFile) {
    const oldKey = thumbnail ? codeKeyFromUrl(thumbnail) : '';
    step('썸네일 업로드 중…');
    const up = await uploadExampleThumbnail(getCredentials, id, thumbnailFile);
    if (oldKey && oldKey !== up.key) { try { await deleteObject(getCredentials, oldKey); } catch (e) { /* ignore */ } }
    thumbnail = up.url;
  }

  // 그림 원본 확장자 — 새 파일이면 그 파일에서, 수정(파일 유지)이면 기존 meta 에서.
  const sourceExt = kind === 'picture'
    ? (normalizeSourceExt(example.sourceExt || example.file) || (prevEx ? prevEx.sourceExt : ''))
    : '';

  let sha256;
  let sizeBytes;
  if (fileBuffer) {
    // 확장자만 믿지 않고 본문 시그니처를 확인 — 툴패스는 gzip(코드 프로젝트) 오발행 차단.
    const v = validateBuffer(kind, sourceExt, fileBuffer);
    if (!v.ok) throw new Error(v.error);
    sizeBytes = fileBuffer.byteLength;
    step('체크섬 계산 중…');
    sha256 = await sha256Hex(fileBuffer);
    step('예제 파일 업로드 중…');
    const name = objectNameFor(kind, sourceExt);
    await putObject(getCredentials, objKey(id, name), new Uint8Array(fileBuffer), contentTypeFor(kind, sourceExt));
    // 확장자/종류가 바뀌면 옛 객체가 남는다 — 정리(orphan 방지).
    if (prevEx) {
      const oldName = objectNameFor(prevEx.kind, prevEx.sourceExt);
      if (oldName !== name) { try { await deleteObject(getCredentials, objKey(id, oldName)); } catch (e) { /* ignore */ } }
    }
  } else if (prev) {
    sha256 = prev.sha256;
    sizeBytes = prev.size_bytes;
  } else {
    throw new Error('예제 파일을 업로드하세요.');
  }

  const meta = toAssetMeta({ ...example, kind, sourceExt, thumbnail }, {
    ownerEmail: owner.email,
    sha256,
    sizeBytes,
    defaultLang: owner.defaultLang,
    cats: owner.cats,
    filters: owner.filters,
    uploadedAt: prev ? prev.uploaded_at : new Date().toISOString(),
  });

  step('목록(manifest) 갱신 중…');
  const fresh = await readAssets(ENGRAVE_MANIFEST_KEY);   // RMW: 쓰기 직전 재읽기
  let next = fresh.filter((a) => a.id !== id);
  next.push(meta);

  // 배치 순서 — ★ 같은 payload 안에서만 캐스케이드한다. 그림과 툴패스는 앱에서 서로 다른
  // 섹션이라 번호 공간이 독립이어야 한다(그림 1-1 을 줘도 툴패스 1-1 은 그대로).
  const ord = getOrder(meta);
  if (ord) {
    const scope = next.filter((a) => payloadOf(a) === payloadOf(meta));
    const changed = computeReorder(scope, id, ord.group, ord.index);
    next = next.map((a) => {
      const c = changed[a.id];
      return c ? { ...a, extras: { ...(a.extras || {}), order_group: c.group, order_index: c.index } } : a;
    });
    for (const cid of Object.keys(changed)) {
      if (cid === id) continue;
      const cm = next.find((a) => a.id === cid);
      if (cm) { step('순서 반영 중…'); await putObject(getCredentials, metaKey(cid), JSON.stringify(cm), 'application/json'); }
    }
    if (changed[id]) { meta.extras = next.find((a) => a.id === id).extras; }
  }

  step('메타데이터 저장 중…');
  await putObject(getCredentials, metaKey(id), JSON.stringify(meta), 'application/json');
  await putObject(getCredentials, ENGRAVE_MANIFEST_KEY, JSON.stringify(buildManifest(sortEngrave(next))), 'application/json');
  step('버전 신호 발행 중…');
  await bumpExamplesVersion(getCredentials);             // 앱 fast-path 갱신 트리거
  return meta;
}

/* 삭제 (RMW) — 객체(원본+meta+썸네일) + manifest 엔트리 제거. */
export async function deleteEngraveExample(getCredentials, example, onStep) {
  const id = example.id;
  const step = (s) => { if (onStep) onStep(s); };

  step('목록 확인 중…');
  const assets = await readAssets(ENGRAVE_MANIFEST_KEY);
  const prev = assets.find((a) => a.id === id) || null;
  const ex = prev ? fromAssetMeta(prev) : example;

  step('파일 삭제 중…');
  await deleteObject(getCredentials, objKey(id, objectNameFor(ex.kind, ex.sourceExt)));
  await deleteObject(getCredentials, metaKey(id));
  const thumbUrl = prev && prev.extras && prev.extras.thumbnail;
  if (thumbUrl) { try { await deleteObject(getCredentials, codeKeyFromUrl(thumbUrl)); } catch (e) { /* ignore */ } }

  step('목록(manifest) 갱신 중…');
  const next = assets.filter((a) => a.id !== id);
  await putObject(getCredentials, ENGRAVE_MANIFEST_KEY, JSON.stringify(buildManifest(sortEngrave(next))), 'application/json');
  step('버전 신호 발행 중…');
  await bumpExamplesVersion(getCredentials);
}
